import React from "react";
import CircularProgress from "@mui/material/CircularProgress";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { fetchCheckinRoster, pickRosterName } from "../../api/backend";
import { CheckinAlert, SessionCard, initials } from "./CheckinShell";
import { getPosition } from "./getPosition";

const messageOf = (err, fallback) => err?.response?.data?.message || fallback;

function Note({ children }) {
  return <div className="ck-note"><InfoOutlinedIcon sx={{ fontSize: 15 }} />{children}</div>;
}

/**
 * No-login check-in: the student sees the class roster (only from inside the room), finds their own name and confirms.
 * The instructor reviews every pick afterwards, so the confirmation says so. Renders inside the check-in shell.
 */
export default function RosterPick({ token, handoff, device, session, onDone }) {
  const [phase, setPhase] = React.useState("loading");
  const [error, setError] = React.useState(null);
  const [students, setStudents] = React.useState([]);
  const [query, setQuery] = React.useState("");
  const [choice, setChoice] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(null);
  const position = React.useRef(null);
  const started = React.useRef(false);
  // The ticket outlives the one-minute code and is bound to this browser, so prefer it when there is one.
  const credential = handoff ? { handoff, device } : { token };

  React.useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      const here = await getPosition();
      if (!here) {
        setError("Your location is needed to show the list, because names are only shown inside the room. "
          + "Allow location access and try again, or sign in to check in.");
        setPhase("error");
        return;
      }
      position.current = here;
      try {
        const data = await fetchCheckinRoster({ ...credential, lat: here.lat, lng: here.lng });
        setStudents(data.students ?? []);
        setPhase("ready");
      } catch (err) {
        setError(messageOf(err, "Couldn't load the list. Scan the code again."));
        setPhase("error");
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, handoff, device]);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await pickRosterName({
        ...credential, userId: choice.id, device, lat: position.current.lat, lng: position.current.lng,
      });
      setDone(result);
      setChoice(null);
      if (onDone) onDone(result);
    } catch (err) {
      setChoice(null);
      setError(messageOf(err, "Couldn't check you in. Ask your instructor."));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <>
        <div className="ck-center" role="status">
          <div className="ck-done"><CheckRoundedIcon sx={{ fontSize: 38 }} /></div>
          <div className="ck-eyebrow">Checked in</div>
          <h1>You&apos;re checked in</h1>
          <p>{done.name}</p>
        </div>
        <SessionCard session={done.session?.name ? done.session : session} />
        <Note>Your instructor will review check-ins made this way.</Note>
      </>
    );
  }

  if (choice) {
    return (
      <>
        <div>
          <div className="ck-eyebrow">Confirm</div>
          <h1>Check in as {choice.name}?</h1>
        </div>
        <div className="ck-who">
          <span className="ck-av">{initials(choice.name)}</span>
          <b>{choice.name}</b>
          {session?.name && <span className="ck-who__s">{session.name}</span>}
        </div>
        <Note>
          Only choose your own name. This is recorded with your location, and your instructor will review it.
        </Note>
        <div className="ck-stack" style={{ marginTop: "auto" }}>
          <button type="button" className="ck-btn ck-btn--p" onClick={confirm} disabled={busy}>
            {busy ? "Checking in…" : "Check in"}
          </button>
          <button type="button" className="ck-btn ck-btn--o" onClick={() => setChoice(null)} disabled={busy}>
            Cancel
          </button>
        </div>
      </>
    );
  }

  const matches = students.filter((s) => s.name.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <>
      <div>
        <div className="ck-eyebrow">{session?.name || "Check in"}</div>
        <h1>Find your name</h1>
      </div>

      {phase === "loading" && <div className="ck-spin"><CircularProgress aria-label="Loading names" /></div>}
      {error && <CheckinAlert>{error}</CheckinAlert>}

      {phase === "ready" && students.length === 0 && (
        <div className="ck-empty">Everyone on the roster is already checked in.</div>
      )}

      {phase === "ready" && students.length > 0 && (
        <>
          <span className="ck-inp">
            <SearchIcon sx={{ fontSize: 18 }} />
            <input
              aria-label="Find your name" placeholder="Search by first or last name" value={query}
              onChange={(e) => setQuery(e.target.value)} autoComplete="off"
            />
          </span>
          <div className="ck-list">
            {matches.length === 0 && <div className="ck-empty">No names match. Ask your instructor to add you.</div>}
            {matches.map((s) => (
              <button key={s.id} type="button" className="ck-li" onClick={() => setChoice(s)}>
                <span className="ck-av">{initials(s.name)}</span>
                <div><b>{s.name}</b><span className="ck-li__s">Enrolled</span></div>
                <ChevronRightIcon className="ck-li__chev" sx={{ fontSize: 18 }} />
              </button>
            ))}
          </div>
          <Note>Only students enrolled in this class are listed. Your instructor reviews roster check-ins.</Note>
        </>
      )}
    </>
  );
}
